/* eslint-disable max-lines, max-lines-per-function */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { Skeleton } from 'primereact/skeleton';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './EstimatedVsActualReport.scss';
import dayjs from 'dayjs';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { ESTIMATED_VS_ACTUAL_REPORT } from '../../../Api/resources/endPoint';
import GraphRepresentation from './GraphRepresentation/GraphRepresentation';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const flattenMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.flatMap((menuItem) => (Array.isArray(menuItem?.items) ? menuItem.items : []))
    : []
);

const getFilterMenuItem = (tableConfiguration = [], field) => {
  const column = Array.isArray(tableConfiguration)
    ? tableConfiguration.find((item) => item?.field === field)
    : null;

  if (!column) return null;

  return flattenMenuItems(column.menuItems).find(
    (item) => item?.type === 'filter' && item?.field === field,
  ) || null;
};

const getSelectedOptionLabel = (
  selectedValue,
  { allowIdentifierFallback = true } = {},
) => {
  if (!selectedValue) return '';
  if (typeof selectedValue === 'string') return selectedValue.trim();
  if (typeof selectedValue === 'number') return allowIdentifierFallback ? `${selectedValue}` : '';

  if (isNonEmptyString(selectedValue.name)) return selectedValue.name.trim();
  if (isNonEmptyString(selectedValue.label)) return selectedValue.label.trim();
  if (!allowIdentifierFallback) return '';
  if (selectedValue.value !== undefined && selectedValue.value !== null) {
    return `${selectedValue.value}`.trim();
  }
  if (selectedValue.id !== undefined && selectedValue.id !== null) {
    return `${selectedValue.id}`.trim();
  }

  return '';
};

const getReadableSelectedLabel = (selectedValue = null) => {
  const selectedLabel = getSelectedOptionLabel(selectedValue, {
    allowIdentifierFallback: false,
  });

  if (!selectedLabel) {
    return '';
  }

  if (!selectedValue || typeof selectedValue !== 'object') {
    return /^\d+$/.test(selectedLabel) ? '' : selectedLabel;
  }

  const rawIdentifiers = [
    selectedValue.id,
    selectedValue.value,
  ]
    .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
    .map((value) => `${value}`.trim());

  return rawIdentifiers.includes(selectedLabel.trim()) ? '' : selectedLabel;
};

const cloneSelectedValues = (selectedValues = []) => (
  Array.isArray(selectedValues)
    ? selectedValues
      .filter(Boolean)
      .map((selectedValue) => (
        typeof selectedValue === 'object'
          ? { ...selectedValue }
          : {
            id: selectedValue,
            value: selectedValue,
            label: `${selectedValue}`,
            name: `${selectedValue}`,
          }
      ))
    : []
);

const getOptionLabelFromChoices = (selectedValue, options = []) => {
  if (!selectedValue || !Array.isArray(options) || options.length === 0) {
    return '';
  }

  const selectedOptionKeys = new Set(
    [
      selectedValue?.id,
      selectedValue?.value,
      selectedValue,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim()),
  );

  const matchedOption = options.find((option) => (
    [
      option?.id,
      option?.value,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim())
      .some((value) => selectedOptionKeys.has(value))
  ));

  return getSelectedOptionLabel(matchedOption, { allowIdentifierFallback: false });
};

const getProjectNameFromFilterItem = (filterItem = null) => {
  if (!filterItem || typeof filterItem !== 'object') {
    return '';
  }

  const selectedValue = filterItem.selectedValue || filterItem.selectedValues?.[0];

  return getReadableSelectedLabel(selectedValue)
    || getOptionLabelFromChoices(selectedValue, filterItem.options);
};

const parseRequestPayload = (response = {}) => {
  const requestData = response?.config?.data;

  if (!requestData) return {};

  if (typeof requestData === 'string') {
    try {
      return JSON.parse(requestData);
    } catch {
      return {};
    }
  }

  return typeof requestData === 'object' ? requestData : {};
};

const getProjectNameFromRequestPayload = (response = {}) => {
  const requestPayload = parseRequestPayload(response);
  const payload = requestPayload?.payload;
  const candidateContainers = [
    requestPayload?.filter,
    requestPayload?.filters,
    requestPayload?.filterCriteria,
    requestPayload?.additionalPayloadData,
    payload?.filter,
    payload?.filters,
    payload?.filterCriteria,
    payload?.additionalPayloadData,
    payload,
    requestPayload,
  ].filter(Boolean);

  const projectNameCandidateValue = candidateContainers
    .map((container) => container?.projectName)
    .find(Boolean);
  const projectNameCandidate = Array.isArray(projectNameCandidateValue)
    ? projectNameCandidateValue[0]
    : projectNameCandidateValue;

  if (!projectNameCandidate) {
    return '';
  }

  if (projectNameCandidate?.selectedValue) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValue)
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValue,
        projectNameCandidate.options,
      );
  }

  if (Array.isArray(projectNameCandidate?.selectedValues)) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValues[0])
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValues[0],
        projectNameCandidate.options,
      );
  }

  return getReadableSelectedLabel(projectNameCandidate);
};

const getReportTitleFromResponse = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};
  const payload = responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
  const reportTitle = `${payload.reportTitle || payload.title || ''}`.trim();

  return reportTitle.startsWith('Estimation Vs Actual - ') ? reportTitle : '';
};

const getResponsePayload = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};

  return responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
};

const areEqualByValue = (leftValue, rightValue) => (
  JSON.stringify(leftValue) === JSON.stringify(rightValue)
);

const getReportTitleFromConfiguration = (
  response,
  tableConfiguration = [],
  defaultTitle = 'Estimation Vs Actual',
) => {
  const selectedProjectFilter = getFilterMenuItem(tableConfiguration, 'projectName');
  const responseReportTitle = getReportTitleFromResponse(response);
  const projectName = getProjectNameFromReportTitle(responseReportTitle)
    || getProjectNameFromRequestPayload(response)
    || getProjectNameFromFilterItem(selectedProjectFilter);

  return projectName ? `Estimation Vs Actual - ${projectName}` : defaultTitle;
};

const getAppliedFilterPayload = (tableConfiguration = []) => {
  const projectVertical = getFilterMenuItem(tableConfiguration, 'projectVertical');
  const projectType = getFilterMenuItem(tableConfiguration, 'projectType');
  const projectStatus = getFilterMenuItem(tableConfiguration, 'projectStatus');
  const projectName = getFilterMenuItem(tableConfiguration, 'projectName');
  const activity = getFilterMenuItem(tableConfiguration, 'activity');
  const total = getFilterMenuItem(tableConfiguration, 'total');
  const dateRange = getFilterMenuItem(tableConfiguration, 'dateRange');

  return {
    projectVertical: cloneSelectedValues(projectVertical?.selectedValues),
    projectType: cloneSelectedValues(projectType?.selectedValues),
    projectStatus: cloneSelectedValues(projectStatus?.selectedValues),
    projectName: {
      selectedValues: cloneSelectedValues(
        projectName?.selectedValues || [projectName?.selectedValue],
      ),
    },
    activity: cloneSelectedValues(activity?.selectedValues),
    total: {
      range: Array.isArray(total?.range) ? [...total.range] : [],
    },
    dateRange: {
      range: Array.isArray(dateRange?.range) ? [...dateRange.range] : [],
    },
  };
};

const getProjectNameFromAppliedFilters = (filterPayload = {}) => {
  const projectNameFilter = filterPayload?.projectName;
  const selectedProject = projectNameFilter?.selectedValues?.[0]
    || projectNameFilter?.selectedValue
    || projectNameFilter;

  return getReadableSelectedLabel(selectedProject)
    || getOptionLabelFromChoices(selectedProject, projectNameFilter?.options);
};

const sanitizeExportFileSegment = (value, fallback = 'Report') => {
  const normalizedValue = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalizedValue || fallback;
};

const formatExportTimestamp = (date = new Date()) => {
  const reportDate = dayjs(date);
  return reportDate.format('DD-MM-YYYY');
};

const getProjectNameFromExportFilters = (filters = {}) => {
  if (Array.isArray(filters)) {
    const projectNameFilter = filters.find((filterItem) => filterItem?.field === 'projectName');
    const selectedValue = projectNameFilter?.selectedValue
      || projectNameFilter?.selectedValues?.[0];

    return getReadableSelectedLabel(selectedValue)
      || getSelectedOptionLabel(selectedValue, {
        allowIdentifierFallback: false,
      });
  }

  const selectedProject = filters?.projectName?.selectedValue
    || filters?.projectName?.selectedValues?.[0]
    || filters?.projectName;

  return getReadableSelectedLabel(selectedProject)
    || getSelectedOptionLabel(selectedProject, {
      allowIdentifierFallback: false,
    });
};

const buildActivityExportFileName = () => {
  return `Estimation-Vs-Actual-Report - ${formatExportTimestamp()}`;
};

const buildActivityExportSheetName = (projectName = '') => (
  `Estimation-Vs-Actual- ${sanitizeExportFileSegment(projectName)}`
);

const getProjectNameFromReportTitle = (reportTitle = '') => {
  const titleParts = `${reportTitle}`.split(' - ');
  return titleParts.length > 1
    ? titleParts.slice(1).join(' - ')
      .trim()
    : '';
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton = !isFilterButton
      && !isResetButton
      && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('estimation-vs-actual-reset-btn', isResetButton);
    buttonElement.classList.toggle('estimation-vs-actual-export-btn', isDownloadButton);
  });
};

const DEFAULT_REPORT_TITLE = 'Estimation Vs Actual';
const TABLE_SCROLL_RATIO = 0.7;
const SUMMARY_ROW_ID = 'summary-row';
const INDIAN_NUMBER_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const REPORT_DATE_HEADER_PATTERN
  = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(\d{2}-[A-Za-z]{3}-\d{4})$/;

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${ESTIMATED_VS_ACTUAL_REPORT}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${ESTIMATED_VS_ACTUAL_REPORT}`;
};

const baseURL = fetchBaseURL();

const getColumnHeaderText = (column = {}) => {
  const headerCandidates = [
    column?.header,
    column?.label,
    column?.filterLabel,
    column?.menuLabel,
    column?.displayLabel,
    column?.field,
  ];

  const headerText = headerCandidates.find((candidate) => typeof candidate === 'string');

  return headerText ? headerText.trim() : '';
};

const isActivityDisplayColumn = (column = {}) => column?.field === 'activity';

const isTotalEffortDisplayColumn = (column = {}) => {
  const headerText = getColumnHeaderText(column).toLowerCase();

  return column?.field === 'total' || headerText.startsWith('total effort');
};

const isDateDisplayColumn = (column = {}) => REPORT_DATE_HEADER_PATTERN.test(
  getColumnHeaderText(column),
);

const getNumericDisplayFields = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration
      .filter((column) => isTotalEffortDisplayColumn(column) || isDateDisplayColumn(column))
      .map((column) => column?.field)
      .filter(Boolean)
    : []
);

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 0;
  }

  const isNegativeValue = trimmedValue.startsWith('(') && trimmedValue.endsWith(')');
  const normalizedValue = trimmedValue.replace(/,/g, '').replace(/[()%]/g, '');
  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return isNegativeValue ? -parsedValue : parsedValue;
};

const formatIndianDecimal = (value) => INDIAN_NUMBER_FORMATTER.format(parseNumericValue(value));

const formatNumericFieldsInRow = (row = {}, numericFields = []) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const formattedRow = { ...row };

  numericFields.forEach((field) => {
    formattedRow[field] = formatIndianDecimal(row[field]);
  });

  if (Array.isArray(row.children)) {
    formattedRow.children = row.children.map((childRow) => formatNumericFieldsInRow(
      childRow,
      numericFields,
    ));
  }

  return formattedRow;
};

const formatTableData = (tableData = [], numericFields = []) => (
  Array.isArray(tableData)
    ? tableData.map((row) => formatNumericFieldsInRow(row, numericFields))
    : []
);

const renderStackedHeader = (
  primaryLabel,
  secondaryLabel,
  variant = '',
  tertiaryLabel = '',
) => (
  <span className={`estimation-vs-actual-header-stack ${variant}`.trim()}>
    <span className="activity-report-header-primary">{primaryLabel}</span>
    <span className="activity-report-header-secondary">{secondaryLabel}</span>
    {tertiaryLabel ? (
      <span className="activity-report-header-tertiary">{tertiaryLabel}</span>
    ) : null}
  </span>
);

const buildDisplayHeader = (column = {}) => {
  const headerText = getColumnHeaderText(column);

  if (isTotalEffortDisplayColumn(column)) {
    return renderStackedHeader(
      'Total effort',
      '(hours)',
      'estimation-vs-actual-header-stack--numeric',
    );
  }

  const dateHeaderMatch = headerText.match(REPORT_DATE_HEADER_PATTERN);

  if (dateHeaderMatch) {
    const [, weekDay, dayLabel] = dateHeaderMatch;

    if (column?.isHoliday) {
      return renderStackedHeader(
        'Holiday',
        weekDay,
        'estimation-vs-actual-header-stack--numeric',
        dayLabel,
      );
    }

    return renderStackedHeader(
      weekDay,
      dayLabel,
      'estimation-vs-actual-header-stack--numeric',
    );
  }

  return headerText;
};

const removeInlineSortIcon = (menuItems = [], shouldHideSortIcon = false) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => (
          item?.type === 'sort' && shouldHideSortIcon
            ? {
              ...item,
              showSortButtonInHeader: false,
            }
            : item
        ))
        : menuItem?.items,
    }))
    : []
);

const normalizeFilterSelectionValue = (selectedValue) => {
  if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
    return null;
  }

  if (typeof selectedValue === 'object' && !Array.isArray(selectedValue)) {
    return selectedValue.value
      ?? selectedValue.id
      ?? selectedValue.name
      ?? selectedValue.label
      ?? null;
  }

  return selectedValue;
};

const normalizeMultiSelectMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.template !== 'multiSelect' && item?.template !== 'choice') {
            return item;
          }

          const normalizedSelectedValues = Array.isArray(item?.selectedValues)
            ? item.selectedValues
              .map((selectedValue) => {
                if (item.field !== 'projectName') {
                  return normalizeFilterSelectionValue(selectedValue);
                }

                if (selectedValue && typeof selectedValue === 'object') {
                  return selectedValue;
                }

                const selectedKey = normalizeFilterSelectionValue(selectedValue);
                return item.options?.find((option) => (
                  `${option?.id ?? option?.value}` === `${selectedKey}`
                )) || selectedValue;
              })
              .filter((selectedValue) => selectedValue !== null)
            : item?.selectedValues;

          return {
            ...item,
            selectedValues: normalizedSelectedValues,
          };
        })
        : menuItem?.items,
    }))
    : []
);

const applyActivityReportFilterMetadata = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.field === 'dateRange') {
            return {
              ...item,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                maxRangeDays: 31,
                maxRangeValidationMessage: 'Date range has to be within 31 days.',
              },
            };
          }

          return item;
        })
        : menuItem?.items,
    }))
    : []
);

const ensureMenuItems = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => ({
      ...column,
      menuItems: Array.isArray(column?.menuItems) ? column.menuItems : [],
    }))
    : []
);

const transformTableConfiguration = (tableConfiguration = []) => ensureMenuItems(
  tableConfiguration,
).map((column) => {
  const headerText = getColumnHeaderText(column);
  const shouldShowHeaderActions = false;
  const shouldDefaultPinColumn = isActivityDisplayColumn(column);
  const shouldShowPinButton = false;
  const hideInlineSortIcon
    = shouldShowHeaderActions;

  return {
    ...column,
    filter: shouldShowHeaderActions ? true : column?.filter,
    pinnable: shouldShowHeaderActions ? false : column?.pinnable,
    defaultPinned: shouldShowHeaderActions
      ? shouldDefaultPinColumn
      : column?.defaultPinned,
    isPinned: shouldShowHeaderActions
      ? false
      : column?.isPinned,
    filterVisible: shouldShowHeaderActions
      ? false
      : column?.filterVisible,
    pinVisible: shouldShowHeaderActions
      ? shouldShowPinButton
      : column?.pinVisible,
    header: buildDisplayHeader({
      ...column,
      header: headerText,
    }),
    label: column?.label || headerText,
    filterLabel: column?.filterLabel || headerText,
    menuLabel: column?.menuLabel || headerText,
    displayLabel: column?.displayLabel || headerText,
    menuItems: removeInlineSortIcon(
      applyActivityReportFilterMetadata(
        normalizeMultiSelectMenuItems(column?.menuItems),
      ),
      hideInlineSortIcon,
    ),
  };
});

const buildSummaryRow = (summary = null) => {
  if (!summary || typeof summary !== 'object' || Object.keys(summary).length === 0) {
    return null;
  }

  return {
    ...summary,
    id: summary.id || SUMMARY_ROW_ID,
    rowConfiguration: {
      ...(summary.rowConfiguration || {}),
      isFrozen: true,
      backgroundColor: summary.rowConfiguration?.backgroundColor || '#eceff5',
    },
  };
};

const applyActivityReportHeaderClass = (rootNode) => {
  if (!rootNode) return;

  const headers = rootNode.querySelectorAll('.project-overview-header');
  headers.forEach((headerElement) => {
    headerElement.classList.add('estimation-vs-actual-header');
  });
};

const applyActivityTableViewport = (rootNode) => {
  if (!rootNode) return;

  const tableContainers = rootNode.querySelectorAll('.table-container');
  tableContainers.forEach((tableContainerElement) => {
    tableContainerElement.classList.add('estimation-vs-actual-table-viewport');
  });
};

const applyBodyCellHoverTitles = (rootNode) => {
  if (!rootNode) return;

  const bodyCells = rootNode.querySelectorAll(
    '.estimation-vs-actual-table-utility .p-datatable-tbody > tr > td',
  );

  bodyCells.forEach((td) => {
    const contentElement
      = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;

    const fullText = (contentElement.textContent || '').trim();
    const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

    if (fullText && isTruncated) {
      td.setAttribute('title', fullText);
      contentElement.setAttribute('title', fullText);
      return;
    }

    td.removeAttribute('title');
    if (contentElement !== td) {
      contentElement.removeAttribute('title');
    }
  });
};

const useActivityReportTableEffects = ({
  containerRef,
  setTableScrollHeight,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const updateTableClasses = () => {
      applyActivityTableViewport(rootNode);
      applyActivityReportHeaderClass(rootNode);
      applyBodyCellHoverTitles(rootNode);
      applyToolbarButtonClasses(rootNode);
    };

    const timer = setTimeout(updateTableClasses, 500);
    const observer = new MutationObserver(updateTableClasses);
    const tableContainer = rootNode?.querySelector('.estimation-vs-actual-table-utility');

    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * TABLE_SCROLL_RATIO)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, setTableScrollHeight]);
};

const useProjectNameTitleSync = ({
  appliedFilterPayload,
  projectNameFilterUrl,
  accessToken,
  utilityUserInfo,
  setReportTitle,
  setProjectOptions,
}) => {
  useEffect(() => {
    const selectedProject = appliedFilterPayload?.projectName;
    const selectedProjectId = selectedProject?.id
      ?? selectedProject?.value
      ?? selectedProject?.selectedValues?.[0]
      ?? null;
    const selectedProjectName = `${selectedProject?.name || selectedProject?.label || ''}`.trim();

    if (!selectedProjectId || !projectNameFilterUrl || !accessToken) {
      return undefined;
    }

    if (selectedProjectName && selectedProjectName !== `${selectedProjectId}`.trim()) {
      setReportTitle(`Estimation Vs Actual - ${selectedProjectName}`);
      return undefined;
    }

    let isMounted = true;

    const fetchProjectName = async () => {
      try {
        const response = await fetch(projectNameFilterUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: accessToken,
          },
          body: JSON.stringify({
            payload: {
              searchQuery: '',
              userInfo: utilityUserInfo,
              ...appliedFilterPayload,
            },
          }),
        });

        const responsePayload = await response.json();
        const projectOptions = Array.isArray(responsePayload?.data) ? responsePayload.data : [];
        if (isMounted && setProjectOptions) {
          setProjectOptions(projectOptions);
        }

        const matchedProject = projectOptions.find((projectOption) => (
          [`${projectOption?.id ?? ''}`.trim(), `${projectOption?.value ?? ''}`.trim()]
            .includes(`${selectedProjectId}`.trim())
        ));
        const matchedProjectName = `${matchedProject?.name || matchedProject?.label || ''}`.trim();

        if (isMounted && matchedProjectName) {
          const newTitle = `Estimation Vs Actual - ${matchedProjectName}`;
          setReportTitle((prevTitle) => {
            if (prevTitle === newTitle) return prevTitle; // Prevent redundant re-render
            return newTitle;
          });
        }
      } catch {
        // Keep the current title if the lookup endpoint fails.
      }
    };

    fetchProjectName();

    return () => {
      isMounted = false;
    };
  }, [
    accessToken,
    appliedFilterPayload,
    projectNameFilterUrl,
    setReportTitle,
    utilityUserInfo,
  ]);
};

const EstimatedVsActualReport = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const userProfileRef = useRef(userProfile);
  userProfileRef.current = userProfile;

  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
  const [appliedFilterPayload, setAppliedFilterPayload] = useState({});
  const [projectNameFilterUrl, setProjectNameFilterUrl] = useState('');
  const [chartData, setChartData] = useState({});
  const [tables, setTables] = useState([
    { configuration: null, data: [], toolBarHeading: 'Estimation Vs Actual' },
  ]);
  const [loading, setLoading] = useState(true);
  const [globalFilterColumns, setGlobalFilterColumns] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [globalFilters, setGlobalFilters] = useState([]);
  const requestIdRef = useRef(0); // Track current request to prevent race conditions

  const getFinancialYearRange = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const currentDate = today.getDate();

    const startYear = currentMonth < 3 ? currentYear - 1 : currentYear;
    const endYear = currentYear;

    const fromDate = new Date(startYear, 3, 1);
    const toDate = new Date(endYear, currentMonth, currentDate);

    const formatDate = (date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - (offset * 60 * 1000));
      return localDate.toISOString().split('T')[0];
    };

    return [formatDate(fromDate), formatDate(toDate)];
  };
  const initialFilterPayload = [{'field': 'projectType', 'selectedValues': [1]}, // Default FPP project type
    {'field': 'activeDate', 'range': getFinancialYearRange()},
  ];

  const utilityUserInfo = useMemo(() => ({
    emailId: userProfile?.email,
    permissions: userProfile?.permissions,
    roleDetails: userProfile?.roleDetails,
    employeeId: userProfile?.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);

  useActivityReportTableEffects({
    containerRef,
    setTableScrollHeight,
  });

  useProjectNameTitleSync({
    appliedFilterPayload,
    projectNameFilterUrl,
    accessToken: userProfile?.access_token,
    utilityUserInfo,
    setReportTitle,
    setProjectOptions,
  });

  const handleFilterUpdate = ({ action, tableConfiguration }) => {
    if (action === 'reset') {
      setGlobalFilters([]);
      setGlobalFilterColumns([]);
      return;
    }
    if (action !== 'apply') return;
    if (Array.isArray(tableConfiguration) && tableConfiguration.length > 0) {
      setGlobalFilterColumns(tableConfiguration.filter((col) => col.isGlobalFilter));
    }
  };

  const buildSelectedProjectExportFileName = useCallback(() => {
    return buildActivityExportFileName();
  }, [appliedFilterPayload, reportTitle]);

  const buildSelectedProjectExportSheetName = useCallback((exportContext = {}) => {
    const selectedProjectName = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters)
      || 'Report';

    return buildActivityExportSheetName(selectedProjectName);
  }, [appliedFilterPayload, reportTitle]);

  const validateFilters = (appliedFilters = []) => {
    const deviationPercentItem = appliedFilters.find(data => data && data.field === 'deviationPercent');

    const hasPercentValue = deviationPercentItem?.selectedValue !== undefined
      || (Array.isArray(deviationPercentItem?.selectedValues) && deviationPercentItem.selectedValues.length > 0);

    const hasDeviationType = appliedFilters.some(data => data && data.field === 'deviationType');

    if (hasPercentValue && !hasDeviationType) {
      toast.error('Deviation type is required when deviation percent is selected.');
      return false; // Validation failed
    }

    return true;
  };


  const handleGlobalFilterApply = async (appliedFilters) => {
    const currentUserProfile = userProfileRef.current;
    if (!currentUserProfile?.email) return;

    if (!validateFilters(appliedFilters)) return;

    // Increment request ID to track this specific request
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    if (isFirstLoad) {
      appliedFilters = [...initialFilterPayload, ...appliedFilters];
    }
    setGlobalFilters(appliedFilters || []); // Setting state to pass down to TableUtility for export
    setLoading(true);
    const filterPayload = {
      filter: appliedFilters || [],
      userInfo: {
        emailId: currentUserProfile.email,
        permissions: currentUserProfile.permissions,
        roleDetails: currentUserProfile.roleDetails,
        employeeId: currentUserProfile.employeeId,
        globalColors: currentUserProfile?.siteConfiguration?.global_colors,
      },
    };

    try {
      const res = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': currentUserProfile?.access_token || localStorage.getItem('authToken') || '',
        },
        body: JSON.stringify(filterPayload),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || 'Failed to load data. Please try again.');
      }

      const payload = getResponsePayload({ data: json });
      const tableConfiguration = transformTableConfiguration(payload.tableConfiguration || []);
      const numericDisplayFields = getNumericDisplayFields(tableConfiguration);
      const rawTableData = Array.isArray(payload.tableData) ? payload.tableData : [];
      const formattedTableData = formatTableData(rawTableData, numericDisplayFields);
      const formattedSummary = formatNumericFieldsInRow(payload.summary, numericDisplayFields);
      const summaryRow = formattedTableData.length > 0 ? buildSummaryRow(formattedSummary) : null;
      const tableData = summaryRow ? [...formattedTableData, summaryRow] : formattedTableData;

      const nextAppliedFilterPayload = getAppliedFilterPayload(tableConfiguration);
      const projectNameFilterItem = getFilterMenuItem(tableConfiguration, 'projectName');
      const nextReportTitle = getReportTitleFromConfiguration({ data: json }, tableConfiguration);
      const nextProjectNameFilterUrl = projectNameFilterItem?.url || '';

      setReportTitle((prev) => (prev === nextReportTitle ? prev : nextReportTitle));
      setAppliedFilterPayload((prev) => (areEqualByValue(prev, nextAppliedFilterPayload) ? prev
        : nextAppliedFilterPayload));
      setProjectNameFilterUrl((prev) => (prev === nextProjectNameFilterUrl ? prev : nextProjectNameFilterUrl));

      const tableRowCount = Math.max(tableData.length, 1);
      const tableResult = {
        configuration: {
          generalSettings: {
            enableApiFiltering: false,
            baseURL,
            exportHeaderUnmerge: true,
            apiEndpoints: {
              download: {
                endpoint: 'export',
                enabled: true,
                filename: buildSelectedProjectExportFileName,
                sheetName: buildSelectedProjectExportSheetName,
                reportTitle: buildSelectedProjectExportFileName,
              },
            },
            showToolBar: true,
            toolBarSettings: {
              showToolBarHeading: true,
              toolBarHeading: nextReportTitle || DEFAULT_REPORT_TITLE,
              showDownloadExcel: true,
              showDownloadCsv: false,
              downloadExcelFileName: buildSelectedProjectExportFileName,
              downloadExcelSheetName: buildSelectedProjectExportSheetName,
              showFilterOption: false,
              showAddNew: false,
              showColumnCustomize: false,
              showHelp: false,
              showSearch: false,
              showSaveView: false,
              showReset: false,
              showExpandCollapse: true,
            },
            userInfo: {
              emailId: currentUserProfile.email,
              permissions: currentUserProfile.permissions,
              roleDetails: currentUserProfile.roleDetails,
              employeeId: currentUserProfile.employeeId,
              globalColors: currentUserProfile?.siteConfiguration?.global_colors,
            },
            handler: {
              onFilterUpdate: handleFilterUpdate,
            },
            showPagination: false,
            headers: {
              token: currentUserProfile?.access_token || localStorage.getItem('authToken') || '',
            },
          },
          apiEndpoints: {
            getAllViews: { endpoint: '', enabled: false },
            getFilteredData: { endpoint: '', enabled: false },
            saveOrUpdateView: { endpoint: '', enabled: false },
            deleteView: { endpoint: '', enabled: false },
            download: {
              endpoint: 'export',
              enabled: true,
              filename: buildSelectedProjectExportFileName,
              sheetName: buildSelectedProjectExportSheetName,
              reportTitle: buildSelectedProjectExportFileName,
            },
          },
          tableSettings: {
            showSubRows: true,
            enableTreeTable: false,
            size: 'small',
            loaderSkeleton: { rows: 10, bodyCells: 2, columns: 5 },
            pageSize: tableRowCount,
            showPageSizeSelector: false,
            paginationConfig: { page: 1, limit: tableRowCount },
            showExpandCollapse: false,
            tableConfig: tableConfiguration,
            tableScrollHeight,
            maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
            tableClassName: 'custom-table-estimation-vs-actual',
          },
        },
        data: tableData,
        toolBarHeading: 'Estimation Vs Actual',
        useCustomBodyRenderer: false,
      };

      // Only update state if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setGlobalFilterColumns(tableConfiguration.filter((col) => col.isGlobalFilter));
        setChartData(json?.chartData || payload?.chartData || {});
        setTables([tableResult]);
        isFirstLoad && setIsFirstLoad(false);
        setLoading(false);
      }
    } catch (err) {
      // Only show error if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        toast.error(err.message || 'An error occurred. Please try again.', {
          position: 'top-center',
          style: { backgroundColor: '#bc4545', color: '#FFFFFF', borderRadius: '12px', fontFamily: 'Mukta' },
        });
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (userProfile?.email) {
      handleGlobalFilterApply([]);
    }
  }, [Boolean(userProfile?.email)]);

  if (!userProfile?.access_token) {
    return null;
  }

  const LocalSkeleton = () => (
    <div className="skeleton-loader">
      {[1, 2].map((key) => (
        <div key={key} className="skeleton-card">
          <div className="skeleton-header">
            <Skeleton width="180px" height="1.5rem" />
            <div style={{ display: 'flex', gap: '8px' }}>
              <Skeleton width="80px" height="1.5rem" />
              <Skeleton width="80px" height="1.5rem" />
            </div>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Skeleton width="100%" height="2rem" />
            <Skeleton width="100%" height="2rem" />
            <Skeleton width="100%" height="2rem" />
            <Skeleton width="100%" height="2rem" />
            <Skeleton width="100%" height="2rem" />
          </div>
        </div>
      ))}
    </div>
  );

  const globalFilterColumnsWithOptions = useMemo(() => {
    if (!projectOptions || projectOptions.length === 0) return globalFilterColumns;
    return globalFilterColumns.map(col => {
      if (col.field === 'projectName') {
        return { ...col, options: projectOptions };
      }
      return col;
    });
  }, [globalFilterColumns, projectOptions]);

  return (
    <div className="estimation-vs-actual-outer-layer filter-class" ref={containerRef}>
      <div className="estimation-vs-actual-table-container">
        <div className="estimation-vs-actual-table-utility">
          {loading ? <LocalSkeleton />
            : <>
              <TableUtility
                tables={tables.map((tbl) => ({
                  configuration: tbl.configuration ? {
                    ...tbl.configuration,
                    tableSettings: {
                      ...tbl.configuration.tableSettings,
                      tableScrollHeight,
                    },
                  } : tbl.configuration,
                  data: tbl.data,
                  useCustomBodyRenderer: typeof tbl.useCustomBodyRenderer === 'boolean' ? tbl.useCustomBodyRenderer
                    : false,
                }))}
                externalFilters={globalFilters}
                globalFilterConfig={globalFilterColumnsWithOptions}
                showGlobalFilter
                showFilterOption
                showTieredMenu
                showDownloadExcel={true}
                showMultiTableExport={false}
                onGlobalFilterApply={handleGlobalFilterApply}
                isLoading={loading}
                globalFilterAlignRight
                renderGlobalFilterTrigger={(globalFilterTrigger) => (
                  <>
                    <div className="estimation-page-header">
                      <span>Estimation Vs Actual MIS</span>
                      <div className="estimation-page-header-actions">
                        {globalFilterTrigger}
                      </div>
                    </div>
                    <GraphRepresentation chartData={chartData} />
                  </>
                )}
              />
              <div className='total-count'>
                Total Project : {tables[0].data.length > 0 ? tables[0].data.length - 1 : 0}
              </div>
            </>
          }
        </div>
      </div>
    </div>
  );
};

export default EstimatedVsActualReport;
